import { useTranslation } from 'react-i18next';

import {
    Badge,
    Body1,
    Flex,
    IconPrescription,
    IconReport,
    Image,
    Stack,
    Subtitle1,
} from '@myphx-engineering/phx-design-system';

import { drugDescriptionMinWidth } from '../../../constants';
import {
    useDrugProgram,
    useMedicationConfigOverride,
} from '../../../contexts/DrugProgramProvider';
import { useFillPathway } from '../../../contexts/fill/use-fill-prescription';
import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import {
    CoverageStatus,
    type DigitallyReceivedPrescriberOrderInfoFragment,
} from '../../../graphql/generated/graphql';
import { DrugKeys } from '../../../util/config/constants';
import { getDrugConfigByMedication } from '../../../util/config/get-drug-config-by-medication';
import { formatMedicationQuantity } from '../../../util/formatting/prescriber-order';
import { getProductDescription } from '../../../util/formatting/prescriber-order/get-product-description';
import {
    getNdcImage,
    getNdcImageMaxDimensions,
    getNdcImageOrientation,
} from '../../../util/get-ndc-image';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

type PatientMedicationDetailsHeaderProps = {
    patientName: string;
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

const PatientMedicationDetailsHeader = ({
    patientName,
    prescriberOrder,
}: PatientMedicationDetailsHeaderProps) => {
    const { t, i18n } = useTranslation('lilly');
    const drugProgram = useDrugProgram();
    const configOverride = useMedicationConfigOverride();
    const { pathway } = useFillPathway();

    const medication = getFragment(prescriberOrder.authorizedMedication);

    const drugConfig = getDrugConfigByMedication(drugProgram, medication);
    const ndcKey =
        medication.__typename === 'UnknownMedication' && medication.originalNdc
            ? medication.originalNdc
            : medication.id;
    const medicationOverride = configOverride.ndcInfo[ndcKey];
    const quantity = formatMedicationQuantity(
        prescriberOrder.quantity,
        medication,
        medicationOverride
    );

    const productDescription = getProductDescription(
        prescriberOrder,
        i18n,
        medicationOverride
    );

    const { coverageInfo } = prescriberOrder;

    if (!drugConfig) {
        return null;
    }

    const image = getNdcImage(drugConfig, ndcKey, configOverride);
    const imageOrientation = getNdcImageOrientation(ndcKey, configOverride);
    const { maxHeight, maxWidth } = getNdcImageMaxDimensions(imageOrientation);

    const showPriorAuthBadge =
        coverageInfo?.coverageStatus === CoverageStatus.PriorAuthRequired &&
        pathway !== 'fallback' &&
        pathway !== 'self-pay';

    return (
        <Stack gap="xxs">
            <Flex align="center" gap="xxs">
                <IconPrescription color="gray" />

                <Body1>
                    {t('order.forUser', {
                        name: patientName,
                    })}
                </Body1>
            </Flex>

            <Flex
                direction={imageOrientation === 'portrait' ? 'row' : 'column'}
                justify={
                    imageOrientation === 'portrait'
                        ? 'space-between'
                        : undefined
                }
                wrap={imageOrientation === 'portrait' ? 'wrap' : undefined}
                gap="xxs"
            >
                <Stack gap="xxs" miw={drugDescriptionMinWidth} flex={1}>
                    <Subtitle1>{productDescription}</Subtitle1>
                    <ConditionalComponent
                        condition={
                            drugConfig.drugKey === DrugKeys.ZepboundKwikPen
                        }
                    >
                        <Body1 bold highContrast>
                            {t('order.singlePatientUse')}
                        </Body1>
                    </ConditionalComponent>
                    <Body1>
                        {t('order.quantity', {
                            quantity,
                        })}
                    </Body1>
                </Stack>
                <Flex w="auto">
                    <Image
                        src={image}
                        aria-hidden="true"
                        fit="contain"
                        maw={maxWidth}
                        mah={maxHeight}
                        w="auto"
                    />
                </Flex>
            </Flex>

            {showPriorAuthBadge ? (
                <Flex>
                    <Badge
                        color="warning"
                        leftSection={<IconReport />}
                        variant="transparent"
                    >
                        {t('priorAuth.notice.button')}
                    </Badge>
                </Flex>
            ) : null}
        </Stack>
    );
};

export default PatientMedicationDetailsHeader;
